import type { Directive, DirectiveBinding } from 'vue'
import { watchEffect } from 'vue'
import { useAuthStore } from '@/stores/auth'

type PermissionValue = string | string[] | undefined | null

const ROLE_LABELS: Record<string, string> = {
  admin: '管理员', teacher: '教师',
}
const PERMISSION_LABELS: Record<string, string> = {
  'homework:create': '创建作业',
  'question:manage': '管理题库',
  'class:manage': '管理班级',
  'student:manage': '管理学生',
  'analytics:view': '查看分析',
  'grading:execute': '执行批改',
}

function formatPermissionHint(value: PermissionValue, arg: string): string {
  if (!value) return ''
  const values = Array.isArray(value) ? value : [value]
  if (arg === 'permission') {
    const labels = values.map(v => PERMISSION_LABELS[v] || v).filter(Boolean)
    return labels.length ? `需要${labels.join('、')}权限` : '需要更高权限'
  }
  if (arg === 'role') {
    const labels = values.map(v => ROLE_LABELS[v] || v).filter(Boolean)
    return labels.length ? `需要${labels.join('或')}角色` : '需要更高角色权限'
  }
  return '需要更高权限'
}

function checkPermission(binding: DirectiveBinding): boolean {
  const authStore = useAuthStore()
  const arg = binding.arg || 'permission'
  const value: PermissionValue = binding.value
  if (!value) return true
  const values = Array.isArray(value) ? value : [value]
  if (arg === 'role') return values.some(role => authStore.hasRole(role))
  return values.some(perm => authStore.hasPermission(perm))
}

export const vPermission: Directive<HTMLElement, PermissionValue> = {
  mounted(el: HTMLElement, binding: DirectiveBinding<PermissionValue>) {
    const stop = watchEffect(() => {
      const hasAccess = checkPermission(binding)

      if (hasAccess) {
        // 移除指令设置的 disabled attribute, Vue 的 :disabled 绑定不受影响
        el.removeAttribute('disabled')
        el.removeAttribute('title')
        el.classList.remove('btn-permission-locked')
      } else {
        const hint = formatPermissionHint(binding.value, binding.arg || 'permission')
        el.setAttribute('disabled', 'true')
        el.setAttribute('title', hint)
        el.classList.add('btn-permission-locked')
      }
    })
    ;(el as any)._vPermissionStop = stop
  },

  unmounted(el: HTMLElement) {
    const stop = (el as any)._vPermissionStop
    if (stop) stop()
  },
}

export default vPermission
